import { useFormik } from "formik";
import { useState, useRef } from "react";
import { CredentialMgmtDataService } from "../../services/CredentialMgmtDataService";
import { _forgotPasswordSuccessMessage, _unknownError } from "../../helpers/StatusMessages/StatusMessages";
import { useConfig } from "../../contexts/ConfigContext";
import "./MsForgotPassword.css";
import * as Yup from "yup";
import { validationSchema_email } from "../../helpers/ValidationSchema/ValidationSchema";
import { Box, Button, Container, Grid, TextField, Typography } from "@mui/material";
import MsShimmer from "../MsShimmer/MsShimmer";
import StatusScreenMs from "../StatusScreenMs/StatusScreenMs";
import SetPasswordInstructions from "../SetPasswordInstructions/SetPasswordInstructions";
import { useTenant } from "../../contexts/TenantService";
import { Translate } from "../../helpers/TranslatedComponent";
import { T_KEYS } from "../../locales/translationKeys";
import { useAppTheme } from "../../theme/useTheme";

const MsForgotPassword = () => {
  const credentialService = CredentialMgmtDataService();
  const [validStatus, setValidStatus] = useState({ step: "Init" });
  const theme = useAppTheme();
  const { config } = useConfig();
  const [isLoading, setIsloading] = useState(false);
  const ignoreFirstBlur = useRef(true);
  const hasManuallyFocused = useRef(false);
  const { tenantSvc } = useTenant();
  const handleForgotPassword = (values) => {
    setIsloading(true);
    credentialService
      .forgotPassword("/accounts/operations/forgot-password", values)
      .then((response) => {
        setIsloading(false);
        setValidStatus({
          status: response.status,
          step: "ForgotPassword",
          message: _forgotPasswordSuccessMessage,
        });
      })
      .catch((error) => {
        setIsloading(false);
        setValidStatus({
          status: error?.response?.status || 500,
          message: _unknownError,
        });
      });
  };
  const formik = useFormik({
    initialValues: { email: "" },
    validationSchema: Yup.object().shape({
      email: validationSchema_email.email,
    }),
    onSubmit: (values) => {
      handleForgotPassword(values);
    },
  });

  if (!config || isLoading) return <MsShimmer />;
  if (!validStatus) return <MsShimmer />;
  if (validStatus.status === 400 || validStatus.status === 500) return <StatusScreenMs status={validStatus} />;
  if (validStatus.status === 200)
    return (
      <SetPasswordInstructions
        onResend={() => handleForgotPassword(formik.values)}
        navigationUrl={tenantSvc?.authExitUrl}
        showBackToLoginAvl={true}
        showResendAvl={true}
      />
    );

  return (
    <Grid className="ms-set-password-container">
      <div className="ms-wl-bank-logo" data-testid="svg-icon">
        {theme?.assets?.logo && <img src={theme.assets.logo} alt="Worldline" />}
      </div>
      <Grid item xs={7} className="right-gradient" data-testid="right-gradient-circle">
        {theme?.assets?.rightCircle && <img src={theme.assets.rightCircle} alt="right-circular-gradient" />}
      </Grid>
      <div className="ms-set-password-left">
        <Container className="ms-form-container">
          <Box>
            <Typography
              variant="h5"
              fontWeight={600}
              className="back-ms"
              onClick={() => window.location.replace(tenantSvc?.authExitUrl)}
            >
              <span>
                <img src={theme?.assets?.backIcon} alt="back" />
              </span>
              <Translate tKey={T_KEYS.MS.BACK} />
            </Typography>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              <Translate tKey={T_KEYS.MS.RESET_PASSWORD} />
            </Typography>
            <Typography variant="body1" gutterBottom>
              <Translate tKey={T_KEYS.MS.RESET_PASSWORD_DIRECTION_MESSAGE} />
            </Typography>
            <form onSubmit={formik.handleSubmit} noValidate>
              <TextField
                fullWidth
                id="email"
                name="email"
                label="E-mail"
                className={`password-input-ms ${formik.errors.email ? "error" : ""}`}
                value={formik.values.email}
                onChange={(e) => {
                  formik.handleChange(e);
                  formik.setFieldTouched("email", true, false);
                }}
                onFocus={() => {
                  if (!ignoreFirstBlur.current) {
                    hasManuallyFocused.current = true;
                  }
                }}
                onBlur={(e) => {
                  if (ignoreFirstBlur.current) {
                    ignoreFirstBlur.current = false;
                    return;
                  }
                  if (hasManuallyFocused.current) {
                    formik.setFieldTouched("email", true);
                  }
                  formik.handleBlur(e);
                }}
                error={formik.touched.email && Boolean(formik.errors.email)}
                autoFocus
                margin="normal"
                sx={{
                  "& label": {
                    color: formik.touched.email && formik.errors.email ? "#d50000" : "#3c3c3c",
                  },
                  "& label.Mui-focused": {
                    color: formik.touched.email && formik.errors.email ? "#d50000" : "#3c3c3c",
                  },
                  "& label.MuiInputLabel-shrink": {
                    transform: "translate(14px, 8px) scale(0.75)",
                  },
                  "& .MuiOutlinedInput-root": {
                    alignItems: "flex-start",
                    paddingTop: "16px",
                  },
                  "& fieldset legend": {
                    display: "none",
                  },
                  "& fieldset": {
                    border: "none",
                  },
                }}
              />
              <span className="validation-error-ms">
                {formik.touched.email && formik.errors.email ? (
                  <>
                    <span className="error-exclamation-ms">!</span>
                    <span className="error-text-ms">{formik.errors.email}</span>
                  </>
                ) : (
                  " "
                )}
              </span>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                type="submit"
                sx={{ mt: 3 }}
                disabled={!formik.isValid || isLoading || formik.values.email === ""}
              >
                <Translate tKey={T_KEYS.MS.SEND} />
              </Button>
            </form>
          </Box>
        </Container>
      </div>
    </Grid>
  );
};

export default MsForgotPassword;
